import { Box } from '@mui/material';
import { useSelector } from 'react-redux';

import { ItemAvailabilityStatus } from 'entities/CatalogItem/lib/itemAvailability';
import { CatalogItemSource } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { getUserIsMaintenanceTechnician } from 'entities/User/model/selectors/userSelectors';
import { FindReplacementLink } from 'shared/ui/FindReplacementLink/FindReplacementLink';

import { CatalogItemCheckboxSection } from './PropertyCatalogItemCheckboxSection';

interface PropertyCatalogItemActionSlotProps {
    showCheckbox: boolean;
    isAvailable: boolean;
    availabilityStatus?: ItemAvailabilityStatus;
    source: CatalogItemSource;
    isMoveToOrderMode: boolean;
    showQtyCheckbox: boolean;
    isActive: boolean;
    qty: number;
    itemIdToEdit?: string;
    selectionKey: string;
    itemUid: string;
    catalogItemId?: string;
    quickEdit?: boolean;
    isInOrder?: boolean;
    categoryId?: string;
    subCategoryId?: string;
    propertyCategoryId?: string;
    title: string;
    isGlobalCatalog: boolean;
    isMoveToInventoryMode: boolean;
    onLearnMoreActionRequired: () => void;
}

export const PropertyCatalogItemActionSlot = ({
    showCheckbox,
    isAvailable,
    availabilityStatus,
    source,
    isMoveToOrderMode,
    isGlobalCatalog,
    showQtyCheckbox,
    isActive,
    qty,
    itemIdToEdit,
    selectionKey,
    itemUid,
    catalogItemId,
    quickEdit,
    isInOrder,
    categoryId,
    subCategoryId,
    propertyCategoryId,
    title,
    isMoveToInventoryMode,
    onLearnMoreActionRequired,
}: PropertyCatalogItemActionSlotProps) => {
    const isMaintenanceTechnician = useSelector(getUserIsMaintenanceTechnician);

    // Item from property catalog (not global marketplace) — shows "Find Replacement" link instead of checkbox in order flow
    const isUnavailableLocalItem =
        !isAvailable && source === 'catalog' && !isGlobalCatalog;

    // In order flow, unavailable local items hide the checkbox (user must find a replacement).
    // In catalog/inventory flows the checkbox is always shown.
    const isCheckboxSectionAvailable =
        !isUnavailableLocalItem || !isMoveToOrderMode;

    return (
        <Box
            display={showCheckbox || !isAvailable ? 'flex' : 'none'}
            sx={{
                flexDirection: 'column',
                alignItems: 'flex-end',
                justifyContent: 'flex-end',
                alignSelf: 'stretch',
                height: '100%',
                gap: '8px',
                ...(isMoveToOrderMode && {
                    minWidth: '110px',
                }),
            }}
        >
            {isCheckboxSectionAvailable && (
                <CatalogItemCheckboxSection
                    showQtyCheckbox={showQtyCheckbox}
                    isActive={isActive}
                    qty={qty}
                    itemIdToEdit={itemIdToEdit}
                    selectionKey={selectionKey}
                    itemUid={itemUid}
                    catalogItemId={catalogItemId}
                    quickEdit={quickEdit}
                    isInOrder={isInOrder}
                    isMoveToInventoryMode={isMoveToInventoryMode}
                    // Tooltip shown for marketplace unavailable items (all flows)
                    // and for local unavailable items only in inventory flow
                    // (in catalog/order flows, local items show "Find Replacement" link instead)
                    showUnavailableTooltip={
                        !isAvailable &&
                        (!isUnavailableLocalItem || isMoveToInventoryMode)
                    }
                    availabilityStatus={availabilityStatus}
                    onLearnMoreActionRequired={onLearnMoreActionRequired}
                    isAvailable={isAvailable}
                    source={source}
                    categoryId={categoryId}
                    subCategoryId={subCategoryId}
                    propertyCategoryId={propertyCategoryId}
                    title={title}
                />
            )}
            {/* "Find Replacement" link: only for unavailable local items in catalog/order flows.
                Hidden in inventory flow — user is just adding to inventory, not ordering. */}
            {!isAvailable &&
                isUnavailableLocalItem &&
                !isMoveToInventoryMode &&
                !isMaintenanceTechnician && <FindReplacementLink />}
        </Box>
    );
};
